import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SharedTheoryProvider } from './contexts/SharedTheoryContext';
import { StatsProvider } from './contexts/StatsContext';
import { LearningProvider, useLearning } from './contexts/LearningContext';
import { agendarNotificacoesDoDia } from './utils/notificacoesLocais';
import Navbar from './components/Navbar';
import AdminShell from './components/AdminShell';
import { useLocation } from 'react-router-dom';
import Login from './pages/Login';
import Home from './pages/Home';
import './styles/global.css';
import './styles/theme.css';
import { ThemeProvider } from './contexts/ThemeContext';
import { CatalogProvider, useCatalog } from './contexts/CatalogContext';

const IntegratedStudy = lazy(() => import('./pages/IntegratedStudy'));
const CourseExams = lazy(() => import('./pages/CourseExams'));
const AdminIntegratedStudy = lazy(() => import('./pages/AdminIntegratedStudy'));

const Materias = lazy(() => import('./pages/Materias'));
const Estudo = lazy(() => import('./pages/Estudo'));
const Revisao = lazy(() => import('./pages/Revisao'));
const Dashboard = lazy(() => import('./pages/LearningDashboard'));
const Ranking = lazy(() => import('./pages/Ranking'));
const NovoCard = lazy(() => import('./pages/NovoCard'));
const Configuracoes = lazy(() => import('./pages/Configuracoes'));
const Admin = lazy(() => import('./pages/Admin'));
const Simulado = lazy(() => import('./pages/Simulado'));
const DiscursiveLab = lazy(() => import('./pages/DiscursiveLab'));
const MyCourses = lazy(() => import('./pages/MyCourses'));
const CoursePage = lazy(() => import('./pages/CoursePage'));
const SubjectDashboard = lazy(() => import('./pages/SubjectDashboard'));
const LessonPage = lazy(() => import('./pages/LessonPage'));
const LessonQuestions = lazy(() => import('./pages/LessonQuestions'));
const ErrorNotebook = lazy(() => import('./pages/ErrorNotebook'));
const IntroTrail = lazy(() => import('./pages/IntroTrail'));
const IntroLesson = lazy(() => import('./pages/IntroLesson'));
const MyJourney = lazy(() => import('./pages/MyJourney'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminAcademic = lazy(() => import('./pages/AdminAcademic'));
const AdminOverview = lazy(() => import('./pages/AdminOverview'));
const StudyWorkspace = lazy(() => import('./pages/StudyWorkspace'));
const FlashcardPractice = lazy(() => import('./pages/FlashcardPractice'));
const PracticeExam = lazy(() => import('./pages/PracticeExam'));

const PageFallback = () => <div className="route-loading"><div className="spinner"/><p>Preparando sua experiência...</p></div>;

const RotaProtegida = ({ children }) => {
  const { usuario, carregando } = useAuth();
  if (carregando) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--text2)', fontSize: '0.9rem' }}>Carregando...</p>
        </div>
      </div>
    );
  }
  return usuario ? children : <Navigate to="/login" />;
};

const RotaAdmin = ({ children }) => {
  const { usuario, perfil, carregando } = useAuth();
  if (carregando) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" />
      </div>
    );
  }
  if (!usuario) return <Navigate to="/login" />;
  if (!(perfil?.isAdmin || perfil?.role === 'admin')) return <Navigate to="/" />;
  return children;
};

const Layout = ({ children }) => {
  const { saveError } = useLearning();
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return <AdminShell><Suspense fallback={<PageFallback/>}>{children}</Suspense></AdminShell>;
  return (
  <div className="app">
    <Navbar />
    {saveError && <div role="alert" className="page">{saveError}</div>}
    <main className="main-content"><Suspense fallback={<PageFallback/>}>{children}</Suspense></main>
  </div>
);
};

const LegacyCourseGuard = ({children}) => {
  const {canAccessCourse}=useAuth();
  return canAccessCourse('pcba-investigador')?children:<Navigate to="/estudar" replace/>;
};

function AppRoutes() {
  useCatalog();
  const { usuario, perfil } = useAuth();

  // Reagenda as notificações locais do dia toda vez que o app abre com o usuário logado
  // e a preferência ativada (o SW pode ter sido encerrado pelo navegador enquanto fechado).
  useEffect(() => {
    if (!usuario || !perfil?.notificacoesAtivadas) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    agendarNotificacoesDoDia(perfil.materias, {
      horaInicio: perfil.notificacaoHoraInicio,
      horaFim: perfil.notificacaoHoraFim
    });
  }, [usuario?.uid, perfil?.notificacoesAtivadas, perfil?.notificacaoHoraInicio, perfil?.notificacaoHoraFim]);

  return (
    <Routes>
      <Route path="/login" element={usuario ? <Navigate to="/" /> : <Login />} />
      <Route path="/" element={<RotaProtegida><Layout><Home /></Layout></RotaProtegida>} />
      <Route path="/materias" element={<RotaProtegida><Layout><LegacyCourseGuard><Materias /></LegacyCourseGuard></Layout></RotaProtegida>} />
      <Route path="/estudar" element={<RotaProtegida><Layout><StudyWorkspace /></Layout></RotaProtegida>} />
      <Route path="/questoes" element={<RotaProtegida><Layout><StudyWorkspace /></Layout></RotaProtegida>} />
      <Route path="/flashcards/:courseId" element={<RotaProtegida><Layout><FlashcardPractice /></Layout></RotaProtegida>} />
      <Route path="/pratica/:courseId" element={<RotaProtegida><Layout><PracticeExam /></Layout></RotaProtegida>} />
      <Route path="/estudo-integrado" element={<RotaProtegida><Layout><IntegratedStudy /></Layout></RotaProtegida>} />
      <Route path="/simulados-curso/:courseId" element={<RotaProtegida><Layout><CourseExams /></Layout></RotaProtegida>} />
      <Route path="/admin/estudo-integrado" element={<RotaAdmin><Layout><AdminIntegratedStudy /></Layout></RotaAdmin>} />
      <Route path="/meus-cursos" element={<RotaProtegida><Layout><MyCourses /></Layout></RotaProtegida>} />
      <Route path="/curso/:courseSlug" element={<RotaProtegida><Layout><CoursePage /></Layout></RotaProtegida>} />
      <Route path="/curso/:courseSlug/:subjectSlug" element={<RotaProtegida><Layout><SubjectDashboard /></Layout></RotaProtegida>} />
      <Route path="/aula/:lessonId" element={<RotaProtegida><Layout><LessonPage /></Layout></RotaProtegida>} />
      <Route path="/aula/:lessonId/questoes" element={<RotaProtegida><Layout><LessonQuestions /></Layout></RotaProtegida>} />
      <Route path="/meus-erros" element={<RotaProtegida><Layout><ErrorNotebook /></Layout></RotaProtegida>} />
      <Route path="/trilha-introdutoria" element={<RotaProtegida><Layout><IntroTrail /></Layout></RotaProtegida>} />
      <Route path="/trilha-introdutoria/:lessonId" element={<RotaProtegida><Layout><IntroLesson /></Layout></RotaProtegida>} />
      <Route path="/minha-jornada" element={<RotaProtegida><Layout><MyJourney /></Layout></RotaProtegida>} />
      <Route path="/perfil" element={<RotaProtegida><Layout><Profile /></Layout></RotaProtegida>} />
      <Route path="/estudo/:materiaId" element={<RotaProtegida><Layout><LegacyCourseGuard><Estudo /></LegacyCourseGuard></Layout></RotaProtegida>} />
      <Route path="/revisao" element={<RotaProtegida><Layout><Revisao /></Layout></RotaProtegida>} />
      <Route path="/dashboard" element={<RotaProtegida><Layout><Dashboard /></Layout></RotaProtegida>} />
      <Route path="/ranking" element={<RotaProtegida><Layout><Ranking /></Layout></RotaProtegida>} />
      <Route path="/cards/novo" element={<RotaProtegida><Layout><NovoCard /></Layout></RotaProtegida>} />
      <Route path="/configuracoes" element={<RotaProtegida><Layout><Configuracoes /></Layout></RotaProtegida>} />
      <Route path="/admin" element={<RotaAdmin><Layout><AdminOverview /></Layout></RotaAdmin>} />
      <Route path="/admin/usuarios" element={<RotaAdmin><Layout><Admin /></Layout></RotaAdmin>} />
      <Route path="/admin/relatorios" element={<RotaAdmin><Layout><AdminOverview /></Layout></RotaAdmin>} />
      <Route path="/admin/seguranca" element={<RotaAdmin><Layout><AdminOverview /></Layout></RotaAdmin>} />
      <Route path="/admin/academico" element={<RotaAdmin><Layout><AdminAcademic /></Layout></RotaAdmin>} />
      <Route path="/simulado" element={<RotaProtegida><Layout><StudyWorkspace /></Layout></RotaProtegida>} />
      <Route path="/simulado/pcba" element={<RotaProtegida><Layout><LegacyCourseGuard><Simulado /></LegacyCourseGuard></Layout></RotaProtegida>} />
      <Route path="/discursivas" element={<RotaProtegida><Layout><LegacyCourseGuard><DiscursiveLab /></LegacyCourseGuard></Layout></RotaProtegida>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
        <CatalogProvider>
        <SharedTheoryProvider><StatsProvider><LearningProvider><AppRoutes /></LearningProvider></StatsProvider></SharedTheoryProvider>
        </CatalogProvider></ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
